<script lang="ts" setup>
    const props = defineProps<{ blok: StickyContactFormStoryblok; uuid: string }>();
    import { onClickOutside, useScrollLock } from '@vueuse/core';
    const { t } = useI18n();
    import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';

    enum ContactState {
        Closed,
        Choice,
        ChatBot,
        ContactForm
    }

    enum ContactConfig {
        Chatbot,
        ContactForm,
        Choice
    }

    let isLocked: WritableComputedRef<boolean>;

    const stickyContactElement = useTemplateRef('stickyContactElement');
    const { activate, deactivate } = useFocusTrap(stickyContactElement, { clickOutsideDeactivates: true });

    const contactState = ref<ContactState>(ContactState.Closed);

    const toggle = (_: StickyContactFormStoryblok['FormConfig'][number]) => {
        // TODO: uncomment when chatbot is activated and remove the last line
        // if (config === 'chatbot') {
        //     contactState.value = ContactState.ChatBot;
        // } else if (config === 'form') {
        //     contactState.value = ContactState.ContactForm;
        // }
        contactState.value = ContactState.ContactForm;
    };

    const open = (value: ContactState) => {
        activate();
        contactState.value = value;
        isLocked.value = true;
    };

    const icon = computed(() => {
        if (contactState.value !== ContactState.Closed) {
            return 'lucide:x';
        }

        if (contactFormConfig.value === ContactConfig.ContactForm) {
            return 'lucide:message-circle-question-mark';
        }

        if (contactFormConfig.value === ContactConfig.Chatbot) {
            return 'lucide:headset';
        }
        return 'lucide:message-circle-question-mark';
    });

    const handleClick = () => {
        if (contactState.value !== ContactState.Closed) {
            deactivate();
            contactState.value = ContactState.Closed;
            isLocked.value = false;
            return;
        }

        if (contactFormConfig.value === ContactConfig.ContactForm) {
            open(ContactState.ContactForm);
            return;
        }

        if (contactFormConfig.value === ContactConfig.Chatbot) {
            open(ContactState.ChatBot);
            return;
        }

        open(ContactState.Choice);
    };

    onClickOutside(stickyContactElement, () => {
        contactState.value = ContactState.Closed;
    });

    const ContactFormComponent = computed(() => {
        const contactForm = props.blok?.ContactForm?.[0];

        return contactForm ?? null;
    });

    const contactFormConfig = computed(() => {
        if (!props.blok?.FormConfig?.length) return null;
        // TODO: uncomment when chatbot is activated
        // if (props.blok.FormConfig.length === 2) return ContactConfig.Choice;
        // if (props.blok.FormConfig[0] === 'chatbot') return ContactConfig.Chatbot;
        if (props.blok.FormConfig[0] === 'form') return ContactConfig.ContactForm;
        return null;
    });

    const formOpen = computed(() => contactState.value !== ContactState.Closed);

    onMounted(() => {
        isLocked = useScrollLock(document.documentElement);
    });

    onBeforeUnmount(() => {
        isLocked.value = false;
    });
</script>

<template>
    <div
        v-if="props.blok"
        ref="stickyContactElement"
        class="sticky-contact-form"
        :class="{ 'sticky-contact-form--stacked': (props.blok?.FormConfig?.length ?? 0) > 1 }"
    >
        <div
            v-if="formOpen"
            class="sticky-contact-form__backdrop"
            aria-hidden="true"
            @click="handleClick"
        />

        <Transition name="sticky-contact-form">
            <div
                v-if="formOpen"
                id="sticky-contact-form"
                class="sticky-contact-form__form"
            >
                <Transition
                    name="sticky-contact-form-content"
                    mode="out-in"
                >
                    <div
                        :key="contactState"
                        class="sticky-contact-form__content"
                    >
                        <template v-if="contactState === ContactState.ChatBot">
                            <div class="sticky-contact-form__contact">
                                <Chatbot />
                            </div>
                        </template>

                        <template v-if="contactState === ContactState.ContactForm && ContactFormComponent">
                            <div class="sticky-contact-form__contact">
                                <StoryblokComponent
                                    :blok="ContactFormComponent"
                                    :uuid="props.uuid"
                                />
                            </div>
                        </template>

                        <template v-if="contactState === ContactState.Choice">
                            <StickyContactChoice
                                :form-config="props.blok.FormConfig"
                                @toggle="toggle"
                            />
                        </template>
                    </div>
                </Transition>
            </div>
        </Transition>
        <button
            :aria-expanded="formOpen"
            :aria-label="t(`stickyContactForm.button.${formOpen ? 'close' : 'open'}`)"
            :class="{ open: !formOpen, cancel: formOpen }"
            aria-controls="sticky-contact-form"
            class="sticky-contact-button"
            @click="handleClick"
        >
            <Transition
                name="sticky-contact-button-icon"
                mode="out-in"
            >
                <Icon
                    :key="icon"
                    :name="icon"
                    size="24px"
                    mode="svg"
                />
            </Transition>
        </button>
    </div>
</template>

<style lang="scss" scoped>
    .sticky-contact-form-enter-active,
    .sticky-contact-form-leave-active {
        transition:
            opacity 0.22s $anim-ease,
            transform 0.22s $anim-ease;
        transform-origin: bottom right;
    }

    .sticky-contact-form-enter-from,
    .sticky-contact-form-leave-to {
        opacity: 0;
        transform: translateY(0.5rem) scale(0.98);
    }

    .sticky-contact-form-enter-to,
    .sticky-contact-form-leave-from {
        opacity: 1;
        transform: translateY(0) scale(1);
    }

    .sticky-contact-form-content-enter-active,
    .sticky-contact-form-content-leave-active {
        transition:
            opacity 0.18s $anim-ease,
            transform 0.18s $anim-ease;
    }

    .sticky-contact-form-content-enter-from,
    .sticky-contact-form-content-leave-to {
        opacity: 0;
        transform: translateY(0.35rem);
    }

    .sticky-contact-form-content-enter-to,
    .sticky-contact-form-content-leave-from {
        opacity: 1;
        transform: translateY(0);
    }

    .sticky-contact-form__backdrop {
        position: fixed;
        inset: 0;
        z-index: 99;
        background: transparent;
        cursor: default;
    }

    .sticky-contact-form__form {
        max-height: calc(100dvh - $spacing - $spacing--6);
        padding: $spacing;
        position: fixed;
        right: $spacing--2;
        bottom: calc($spacing--6);
        z-index: 100;
        width: min(22rem, calc(100vw - $spacing--6));
        border-radius: 8px;
        background: $white;
        box-shadow: 0.2rem 0.2rem 0.8rem 0.01rem $gray-transparent;
        overflow-y: auto;
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;
    }

    .sticky-contact-form__content {
        min-height: 1px;
    }

    .sticky-contact-form__form--contact {
        transition: min-height 0.22s $anim-ease;
    }

    @media (prefers-reduced-motion: reduce) {
        .sticky-contact-form-enter-active,
        .sticky-contact-form-leave-active,
        .sticky-contact-form-content-enter-active,
        .sticky-contact-form-content-leave-active {
            transition: none;
        }
    }

    .sticky-contact-button {
        position: relative;
        display: flex;
        justify-content: center;
        margin-bottom: 0;
        align-items: center;
        height: calc($spacing--4 - $spacing);
        width: calc($spacing--4 - $spacing);
        border: 0;
        border-radius: 100rem;
        z-index: 100;
        transition:
            opacity 0.3s $anim-ease,
            transform 0.3s $anim-ease,
            background-color 0.3s $anim-ease;
        box-shadow: 0.2rem 0.2rem 0.8rem 0.01rem $gray-transparent;
        cursor: pointer;

        &:active {
            transform: scale(0.94);
        }

        .sticky-contact-button-icon-enter-active,
        .sticky-contact-button-icon-leave-active {
            transition:
                opacity 0.16s $anim-ease,
                transform 0.16s $anim-ease;
        }

        .sticky-contact-button-icon-enter-from {
            opacity: 0;
            transform: rotate(-45deg) scale(0.85);
        }

        .sticky-contact-button-icon-leave-to {
            opacity: 0;
            transform: rotate(45deg) scale(0.85);
        }

        .sticky-contact-button-icon-enter-to,
        .sticky-contact-button-icon-leave-from {
            opacity: 1;
            transform: rotate(0) scale(1);
        }

        &.open {
            background-color: $brand-primary;
            &:hover,
            &:focus-visible {
                background-color: $brand-primary-dark;
            }

            &:focus-visible {
                outline: 2px solid $color-focus;
                outline-offset: 3px;
            }
        }

        &.cancel {
            background-color: $black;
            &:hover,
            &:focus-visible {
                background-color: $gray;
            }

            &:focus-visible {
                outline: 2px solid $color-focus;
                outline-offset: 3px;
            }
        }
    }

    :deep(.sticky-contact-button *) {
        color: $white;
    }

    @media (prefers-reduced-motion: reduce) {
        .sticky-contact-form-enter-active,
        .sticky-contact-form-leave-active,
        .sticky-contact-form-content-enter-active,
        .sticky-contact-form-content-leave-active,
        .sticky-contact-button-icon-enter-active,
        .sticky-contact-button-icon-leave-active {
            transition: none;
        }
    }

    .sticky-contact-form__contact {
        @media (max-width: $br-m) {
            padding: $spacing;
            position: fixed;
            top: $spacing;
            right: $spacing;
            bottom: $spacing;
            left: $spacing;
            z-index: 100;
            width: auto;
            height: 87vh;
            max-height: none;
            border-radius: 8px;
            background: #fff;
            box-shadow: 0.2rem 0.2rem 0.8rem 0.01rem $gray-transparent;
            overflow-y: auto;
        }
    }
</style>
